import {Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {EnvService} from '../../services/env.service';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {NgSelectComponent} from '@ng-select/ng-select';

/**
 * A remote, paginated select. The endpoint must return a Spring-style page:
 * { content: [], number: 0, last: false, totalElements: 0 }.
 */
@Component({
  selector: 'micro-select-pagination',
  templateUrl: './micro-select-pagination.component.html'
})
export class MicroSelectPaginationComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(NgSelectComponent) select!: NgSelectComponent;
  /** Relative to the configured API URL, or an absolute endpoint. */
  @Input() apiUrl!: string;
  /** Alias for apiUrl, useful where existing code already calls the input url. */
  @Input() url!: string;
  /** Additional query parameters sent on every request. */
  @Input() params: {[key: string]: any} = {};
  /** Backend query parameter used for the search text. */
  @Input() searchParam = 'name';
  @Input() pageSize = 20;
  @Input() debounceTime = 1000;
  @Input() placeholder = 'Select...';
  @Input() valueField = 'name';
  @Input() required = true;
  @Input() disabled = false;
  @Input() requires: any;
  @Input() allLabel: any;
  @Input() allId: any;
  @Input() noMinWidth = false;
  /** Route segment used by the optional jump-to-selected-item link. */
  @Input() routerBase!: string;

  /** The ID returned to the host component after a selection. */
  @Input() id: any;
  /** A previously selected object, used to render the initial label. */
  @Input() defaultOption: any;

  @Output() idChange = new EventEmitter<any>();
  @Output() objectChange = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() loadError = new EventEmitter<any>();

  options: any[] = [];
  selected: any;
  loading = false;
  hasMore = false;
  totalElements = 0;

  private page = 0;
  private searchTerm = '';
  readonly searchTerms = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private requestId = 0;

  constructor(private http: HttpClient, private env: EnvService) {}

  ngOnInit(): void {
    this.setSelectionFromInputs();
    this.subscriptions.add(this.searchTerms.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap(term => {
        this.searchTerm = term || '';
        this.page = 0;
        const requestId = ++this.requestId;
        return this.requestPage(this.page, this.searchTerm, requestId);
      })
    ).subscribe({
      next: response => response.requestId === this.requestId && this.applyPage(response.page, false),
      error: error => this.handleError(error)
    }));

    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultOption || changes.id) {
      this.setSelectionFromInputs();
    }

    const endpointChange = changes.apiUrl || changes.url;
    const configurationChange = changes.params || changes.pageSize;
    if ((endpointChange && !endpointChange.firstChange) || (configurationChange && !configurationChange.firstChange)) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:scroll')
  closeOnPageScroll(): void {
    if (this.select?.isOpen) {
      this.select.close();
    }
  }

  reload(): void {
    if (!this.endpoint) {
      return;
    }

    this.searchTerm = '';
    this.options = [];
    this.hasMore = false;
    this.page = 0;
    this.loading = true;
    const requestId = ++this.requestId;
    this.requestPage(this.page, this.searchTerm, requestId).subscribe({
      next: response => response.requestId === this.requestId && this.applyPage(response.page, false),
      error: error => this.handleError(error)
    });
  }

  search(term: string): void {
    this.searchTerms.next(term || '');
  }

  loadNextPage(): void {
    if (this.loading || !this.hasMore || !this.endpoint) {
      return;
    }

    this.loading = true;
    const requestId = ++this.requestId;
    this.requestPage(this.page + 1, this.searchTerm, requestId).subscribe({
      next: response => response.requestId === this.requestId && this.applyPage(response.page, true),
      error: error => this.handleError(error)
    });
  }

  change(option: any): void {
    this.selected = option || undefined;
    this.id = option ? this.getOptionId(option) : this.allId;
    this.idChange.emit(this.id);
    this.objectChange.emit(option || this.allId);
    this.valueChange.emit(option ? this.getOptionLabel(option) : this.allLabel);
  }

  getOptionId(option: any): any {
    if (option == null) {
      return undefined;
    }
    return typeof option === 'string' ? option : option.id;
  }

  getOptionLabel(option: any): any {
    if (option == null) {
      return undefined;
    }
    return typeof option === 'string' ? option : option[this.valueField];
  }

  trackById = (_: number, option: any) => this.getOptionId(option);

  get canJump(): boolean {
    return !!this.routerBase && this.id !== undefined && this.id !== null && this.id !== '' && this.id !== 'undefined';
  }

  private get endpoint(): string | undefined {
    const endpoint = this.apiUrl || this.url;
    if (!endpoint) {
      return undefined;
    }
    return /^https?:\/\//i.test(endpoint)
      ? endpoint
      : `${this.env.e.url}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  }

  private requestPage(page: number, searchTerm: string, requestId: number) {
    this.loading = true;
    let httpParams = new HttpParams()
      .set('page', String(page))
      .set('size', String(this.pageSize));

    Object.keys(this.params || {}).forEach(key => {
      const value = this.params[key];
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    });

    if (searchTerm && this.searchParam) {
      httpParams = httpParams.set(this.searchParam, searchTerm);
    }

    if (!this.endpoint) {
      return new Subject<{ page: any, requestId: number }>().asObservable();
    }
    
    return this.http.get<any>(this.endpoint, {params: httpParams}).pipe(
      map(result => ({page: result, requestId}))
    );
  }

  private applyPage(result: any, append: boolean): void {
    const content = Array.isArray(result?.content) ? result.content : [];
    this.options = append ? this.mergeOptions(this.options, content) : content;
    this.page = result?.number ?? (append ? this.page + 1 : 0);
    this.totalElements = result?.totalElements ?? this.options.length;
    this.hasMore = result?.last === false && content.length > 0;
    this.loading = false;
    this.setSelectionFromInputs();
  }

  private mergeOptions(existing: any[], incoming: any[]): any[] {
    const ids = new Set(existing.map(option => this.getOptionId(option)));
    return existing.concat(incoming.filter(option => !ids.has(this.getOptionId(option))));
  }

  private setSelectionFromInputs(): void {
    const selectedId = this.id === undefined || this.id === null || this.id === '' ? undefined : this.id;
    const defaultId = this.getOptionId(this.defaultOption);

    if (selectedId !== undefined && defaultId === selectedId) {
      this.selected = this.defaultOption;
      return;
    }

    const matchingOption = this.options.find(option => this.getOptionId(option) === selectedId);
    if (matchingOption) {
      this.selected = matchingOption;
    } else if (selectedId === undefined) {
      this.selected = undefined;
    }
  }

  private handleError(error: any): void {
    this.loading = false;
    this.hasMore = false;
    this.loadError.emit(error);
  }
}
